import { ResponseShapeError } from "@shared/types";

/**
 * The most useful sentence the server sent, out of an axios failure.
 *
 * WHY THIS IS NOT JUST `data.message`. A schema rejection from
 * `errorHandler.ts` always answers `message: "Validation error"` -- a constant,
 * identical for a malformed URL, a two-letter country code that is not two
 * letters, and a missing required field. The part that says WHICH field and WHY
 * lives in a separate `errors` map keyed by zod's issue path. A screen that
 * reads only `message` shows "Validation error" for every one of them, which
 * tells the operator nothing they can act on and sends them to an engineer.
 *
 * Every other error shape -- `AppError` and its subclasses -- puts its real
 * sentence in `message` ("Cannot merge into X because it has been withdrawn
 * from sale"), so `message` remains the fallback rather than the first choice.
 *
 * The `body.` / `params.` / `query.` prefixes are stripped because `validate()`
 * wraps each schema as `{ body: ... }`, so zod reports `body.website` for what
 * the operator knows as Website.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
	// A 2xx the client could not read: the server is fine, the two sides disagree
	// about the shape. Name the field, so the report says where to look.
	if (error instanceof ResponseShapeError) {
		const first = error.issues[0];
		return `The server sent something this screen did not expect${first ? ` (${first.path})` : ""}.`;
	}

	const data = (
		error as {
			response?: {
				data?: { message?: string; errors?: Record<string, string> };
			};
		}
	)?.response?.data;

	const fields = Object.entries(data?.errors ?? {});
	if (fields.length > 0) {
		return fields
			.map(([path, message]) => {
				const field = path.replace(/^(body|params|query)\./, "");
				return field && field !== "value" ? `${field}: ${message}` : message;
			})
			.join("; ");
	}

	return data?.message ?? fallback;
}
